import axios from "axios";
import { DateTime } from "luxon";
import { useState, useEffect, useContext, useCallback } from "react";
import { IconContext } from "react-icons";
import { MdClose, MdDone, MdEdit, MdNotificationsNone, MdInfo, MdOpenInNew, MdAdd } from "react-icons/md";
import { IoMdPersonAdd } from 'react-icons/io';
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import { Card } from "@merchstores/shared/elements/Card";
import { Tab, Tabs, Content } from "@merchstores/shared/elements/Tabs";
import { CTA } from "@merchstores/shared/elements/Cta";
import { ColorPickerInput } from "@merchstores/shared/elements/ColorPickerInput";
import { FormSelect, IFormOptionProps } from "@merchstores/shared/elements/FormSelect";
import { FormInput } from "@merchstores/shared/elements/FormInput";
import { CloudinaryFileInput } from "@merchstores/shared/elements/CloudinaryFileInput";
import { Checkbox } from "@merchstores/shared/elements/Checkbox";
import { ConfirmationModal } from "@merchstores/shared/elements/ConfirmationModal";
import { SchedulerInput } from "@merchstores/shared/elements/SchedulerInput";
import { ActionDropdown } from "@merchstores/shared/elements/ActionDropdown";
import { merchstoreStorefrontClient } from "../../App";
import { Context } from "../../context";
import { IMerchStoreIdProps } from "./index"
import { MerchStoreMemberList } from "./MerchStoreMemberList";
import { ProductSearch } from "../MerchStore/ProductSearch";
import { IMerchStoreProduct, IProductImage, IProductVariant, MerchStoreProductList } from "./MerchStoreProductList";
import { MerchStoreProductEdit } from "./MerchStoreProductEdit";
import { findAndUpdateNetlifySnippet } from "../Netlify/NetlifySnippets";
import { cloudinaryApplyWidthHeight } from "@merchstores/shared/components/Cloudinary";
import { MerchStoreTiles } from "./MerchStoreTiles";
import { newMerchStoreStatusOptions, buildMerchStoreStatusOptions } from './MerchStoreStatusOptions';
import LoadingGif from "../../assets/common/Flask-3s-200px.svg"
import { SHOPIFY_COLLECTION_DELETE_REFRESH_TIME, SHOPIFY_COLLECTION_ADDED_REFRESH_TIME, getMerchstoreProductsByTag } from "../../queries/storefront-graphql";

export const CreateEditMerchStore: React.FC = () => {
  const { id } = useParams<IMerchStoreIdProps>();
  const history = useHistory();
  const { userRole, userEmail } = useContext(Context);
  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm();

  const [, setSelectedStatus] = useState(undefined);
  const [accentColor, setAccentColor] = useState("#fff");
  const [active, setActive] = useState(0);
  const [addProductsModalOpen, setAddProductsModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [disabledPasswords, setDisabledPassword] = useState(false);
  const [disabledProductIds, setDisabledProductIds] = useState(undefined);
  const [formData, setFormData] = useState(false);
  const [formFound, setFormFound] = useState(false);
  const [invite, setInvite] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [merchStore, setMerchStoreDetails] = useState(undefined);
  const [merchstoreCollection, setMerchstoreCollection] = useState(undefined);
  const [merchStoreEditActive, setMerchStoreEditActive] = useState(false);
  const [merchStoreError, setMerchStoreError] = useState(false);
  const [merchStoreLogo, setMerchStoreLogo] = useState(null);
  const [merchStoreName, setMerchStoreName] = useState("");
  const [merchStoreStatusOptions, setMerchStoreStatusOptions] = useState(newMerchStoreStatusOptions());
  const [nameEditActive, setNameEditActive] = useState(false);
  const [newMerchStoreName, setNewMerchStoreName] = useState("");
  const [passwordSelected, setPasswordSelected] = useState(false);
  const [productListLoading, setProductListLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortOptionChosen, setSortOptionChosen] = useState('nameDesc');
  const [storeBannerImage, setStoreBannerImage] = useState(null);
  const [storeBannerImageError, setStoreBannerImageError] = useState(false);
  const [storeLogoError, setStoreLogoError] = useState(false);
  const [tempMerchStoreUrl, setTempStoreUrl] = useState(undefined);

  const domainName = "merchologysolutions.com";
  const iconSize = isMobile ? '16px' : '20px';

  const handleTabSelect = (e: Event) => {
    const target = e.target as HTMLButtonElement;
    const index = target.id ? parseInt(target.id, 0) : null;
    if (index !== active) {
      setActive(index);
    }
  };

  const handleMerchStoreNameChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement;
    setMerchStoreName(target.value);
    setMerchStoreEditActive(true);
  }

  const handleMerchStoreNameSubmit = () => {
    if (merchStoreName) {
      setNewMerchStoreName(merchStoreName);
      setNameEditActive(false);
      setMerchStoreName("");
    } else {
      setNameEditActive(false);
    }
  }

  const handleLogoChange = (logoData: [string]) => {
    if (logoData.length) {
      setMerchStoreLogo(logoData);
      if (storeLogoError) {
        setStoreLogoError(false);
      }
    } else {
      setMerchStoreLogo(null);
    }
  }

  const openAddProductsModal = () => {
    setAddProductsModalOpen(!addProductsModalOpen);
  }

  const onModalClose = () => {
    setAddProductsModalOpen(false);
  }

  const handleBannerImageChange = (bannerImageData: [string]) => {
    if (bannerImageData.length) {
      setStoreBannerImage(bannerImageData);
      if (storeBannerImageError) {
        setStoreBannerImageError(false);
      }
    } else {
      setStoreBannerImage(null);
    }
  }

  const fetchMerchStore = useCallback((id: string) => {
    return new Promise((resolve, reject) => {
      axios.post('/.netlify/functions/fetchMerchStoreById', {
        storeCode: id
      })
        .then(res => {
          if (res.data) {
            if (res.data.password) {
              setPasswordSelected(true);
            }
            setMerchStoreStatusOptions(buildMerchStoreStatusOptions(res.data.status));
            setMerchStoreDetails(res.data);
            if (res.data.storeLogo) {
              setMerchStoreLogo([res.data.storeLogo]);
            }
            if (res.data.bannerImage) {
              setStoreBannerImage([res.data.bannerImage]);
            }
            if (res.data.accentColor) {
              setAccentColor(res.data.accentColor);
            }
            reset();
            resolve(res.data);
          } else {
            toast.error(`No MerchStore found with for ID: ${id}`);
            history.push("/");
            setMerchStoreError(true);
          }
        })
        .catch(err => {
          reject(err);
        })
    })
  }, [reset]);

  const fetchCollection = (storeCode: string) => {
    return new Promise((resolve, reject) => {
      const productTag = `merchstore_code=${storeCode}`;
      merchstoreStorefrontClient.send(getMerchstoreProductsByTag(productTag))
        .then(({ data }: any) => {
          const collectionProducts = data.products.edges.map((productNode: any) => fetchProductNode(productNode.node));
          setMerchstoreCollection(collectionProducts);
          setDisabledProductIds(getBaseProductIds(collectionProducts));
          setProductListLoading(false);
          resolve({ products: data.products.edges, storeCode: storeCode });
        })
        .catch(({ err }: any) => {
          reject(err);
        })
    })
  }

  const getBaseProductIds = (products: IMerchStoreProduct[]): string[] => {
    const result = [] as string[];
    products.forEach(product => {
      const productTags = product.tags;
      const tag = productTags.find((tag: string) => tag.includes('merchstore_base_product_id='));
      if (tag) {
        const baseProductId = tag.substring(tag.indexOf('=') + 1);
        result.push(baseProductId);
      }
    });
    return result;
  }

  const fetchProductNode = (productNode: any): IMerchStoreProduct => {
    const artworkTag = productNode.tags.find((tag: string) => tag.includes('artwork_option='));
    const product = {
      id: productNode.id,
      title: productNode.title,
      handle: productNode.handle,
      createdAt: productNode.createdAt,
      descriptionHtml: productNode.descriptionHtml,
      tags: productNode.tags,
      productType: productNode.productType,
      vendor: productNode.vendor,
      artworkSelected: artworkTag ? parseInt(artworkTag.split('=')[1]) : 1,
      images: productNode.images && productNode.images.edges ? productNode.images.edges.map((imageNode: any) => {
        return {
          id: imageNode.node.id,
          src: imageNode.node.src,
          altText: imageNode.node.altText,
        } as IProductImage
      }) : [],
      variants: productNode.variants && productNode.variants.edges ? productNode.variants.edges.map((variantNode: any) => {
        return {
          id: variantNode.node.id,
          productId: variantNode.node.product.id,
          title: variantNode.node.title,
          price: variantNode.node.price,
          compareAtPrice: variantNode.node.compareAtPriceV2,
          available: variantNode.node.available,
          quantityAvailable: variantNode.node.quantityAvailable,
          image: variantNode.node.image,
          sku: variantNode.node.sku
        } as IProductVariant
      }) : [],
    };

    return product;
  }


  const convertDate = (date: string) => {
    let dt;
    if (date.indexOf("/") > -1) {
      dt = DateTime.fromFormat(date, 'MM/dd/yyyy', { zone: 'utc' });
    }
    else {
      dt = DateTime.fromISO(date, { zone: 'utc' });
    }
    return dt.setZone('utc').toISODate();
  }

  const updateMerchStore = async (data: any) => {

    return new Promise((resolve, reject) => {
      const updatedMerchStore = {
        storeCode: merchStore.storeCode,
        name: newMerchStoreName.length ? newMerchStoreName : merchStore.name,
        status: data["merch-store-status"].length ? data["merch-store-status"] : merchStore.status,
        customerAdmins: merchStore.customerAdmins,
        subdomain: merchStore.subdomain,
        baseCollectionHandle: merchStore.baseCollectionHandle,
        customDomain: merchStore.customDomain,
        storeLogo: merchStoreLogo && merchStoreLogo.length > 0 ? merchStoreLogo[0] : '',
        bannerImage: storeBannerImage && storeBannerImage.length > 0 ? storeBannerImage[0] : '',
        storeMessage: data["store-message"],
        revenue: merchStore.revenue,
        accentColor: accentColor,
        merchologist: merchStore.merchologist ? merchStore.merchologist : "",
        payerType: merchStore.payerType,
        alwaysOpen: merchStore.alwaysOpen,
        processingSchedule: data['store-processing-schedule'].length ? data['store-processing-schedule'] : merchStore.processingSchedule,
        nextProcessingDate: data["store-next-processing-date"] ? convertDate(data["store-next-processing-date"]) : merchStore.nextProcessingDate,
        password: passwordSelected ? data["store-password"] : "",
        artworkOptions: merchStore.artworkOptions,
        shipToOffice: merchStore.shipToOffice
      }

      if (accentColor != merchStore.accentColor) {
        const script = `<style type="text/css">
          button.cta-custom { 
            background-color: ${accentColor} !important; 
          }
          a.store-link:hover {
            color: ${accentColor} !important; 
          }
          .toast-success-notification {
            background-color: ${accentColor} !important; 
          }
          .cart-indicator-icon {
            background-color: ${accentColor} !important; 
          }
          .select-done-icon-container svg{
            color: ${accentColor} !important; 

          }
        </style>`;
        findAndUpdateNetlifySnippet(merchStore.netlifySiteId, "Accent Color", script);
      }

      if (updatedMerchStore.storeLogo) {
        const newStoreLogo = cloudinaryApplyWidthHeight(updatedMerchStore.storeLogo, null, 50);
        updatedMerchStore.storeLogo = newStoreLogo;
      }
      if (updatedMerchStore.bannerImage) {
        const newBannerImage = cloudinaryApplyWidthHeight(updatedMerchStore.bannerImage, 1440, 320);
        updatedMerchStore.bannerImage = newBannerImage;
      }
      axios.post("/.netlify/functions/updateMerchStore", {
        merchStoreData: updatedMerchStore
      })
        .then(merchStoreResp => {
          setMerchStoreStatusOptions(buildMerchStoreStatusOptions(updatedMerchStore.status));

          if (updatedMerchStore.status === "active" && merchStore.status !== "active") {
            setMerchStoreDetails(updatedMerchStore);
          }
          resolve(merchStoreResp);
        })
        .catch(err => {
          reject(err);
        });
    })
  }

  const fetchMembers = (storeCode: string) => {
    return new Promise((resolve, reject) => {
      axios.post('/.netlify/functions/getMemberInfoByMerchStore', {
        storeCode: storeCode,
        sortOptionChosen: sortOptionChosen
      })
        .then(res => {
          if (res.data) {
            if (res.data.length) {
              setMembers(res.data);
            }
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        })
    })
  }

  // check custom fields that don't get submitted through react-hooks-form, return true if any errors, false if none
  const checkCustomParams = () => {
    if (!merchStoreLogo) {
      setStoreLogoError(true);
    }
    return false;
  }

  // because this logic needs to be gated behind a confirmation modal if the MerchStore is set to active, we need to make it it's own function
  const processMerchStore = async (data: any) => {
    if (merchStore) {
      updateMerchStore(data)
        .then(async () => {
          setMerchStoreEditActive(false);
          toast.success("Your MerchStore was successfully updated");
        })
        .catch(err => {
          console.error(err)
          toast.error("There was an error updating your MerchStore. Please try again or contact support.")
        })
    }
  }

  // since the top button lives outside the form 
  const handleMerchStoreSave = async (data: any) => {
    // check for errors outside the form data
    const outsideErrors = checkCustomParams();
    if (!outsideErrors) {
      if (data["merch-store-status"] === "active") {
        if (!merchStore || merchStore.status !== "active") {
          setFormData(data);
          setConfirmationModalOpen(true);
        } else {
          processMerchStore(data);
        }
      } else {
        processMerchStore(data);
      }
    }
  }

  const onMembersAdded = () => {
    fetchMembers(merchStore.storeCode);
  }

  const onProductsAdded = () => {
    setProductListLoading(true);
    setTimeout(() => {
      fetchCollection(merchStore.storeCode);
    }, SHOPIFY_COLLECTION_ADDED_REFRESH_TIME); // Wait some time to get the Shopify Collection refreshed after a product change.
  }

  const onProductsDeleted = () => {
    setProductListLoading(true);
    setTimeout(() => {
      fetchCollection(merchStore.storeCode);
    }, SHOPIFY_COLLECTION_DELETE_REFRESH_TIME); // Wait some time to get the Shopify Collection refreshed after a product change.
  }

  const handlePasswordSelect = () => {
    // Set merchstoreEditActive to true only when switching from selected to unselected.
    // merchstoreEditActive will be set to active when user types in password input
    if (passwordSelected) {
      setMerchStoreEditActive(true);
    }
    setPasswordSelected(!passwordSelected);
  }

  const handleStatusChange = (status: IFormOptionProps) => {
    const currentStatus = merchStore ? merchStore.status : "default";
    if (status.value !== currentStatus) {
      setMerchStoreEditActive(true);
    }
  }

  const handleProcessingSchedule = (type: IFormOptionProps) => {
    const currentSchedule = merchStore ? merchStore.processingSchedule : "";
    if (type.value !== currentSchedule) {
      setMerchStoreEditActive(true);
    }
  }

  const onAccentColorChange = (newColor: string) => {
    setMerchStoreEditActive(true);
    setAccentColor(newColor);
  }

  const deleteProduct = (productId: string) => {
    const convertedProductId = (atob(productId));

    axios.post("/.netlify/functions/deleteProduct", {
      productId: convertedProductId
    })
      .then(() => {
        toast.success('The product has been successfully deleted from your store, but it may take a few moments for it to delete from your product list');
        onProductsDeleted();
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error deleting product")
      })
  }

  useEffect(() => {
    if (id) {
      return fetchMerchStore(id)
        .then((merchStore: any) => { return fetchCollection(merchStore.storeCode) })
        .then((collectionResp: any) => { return fetchMembers(collectionResp.storeCode) })
        .then(() => setTimeout(() => setLoading(false), 200))
        .catch((err: any) => {
          console.error('Malformed MerchStore ID', err);
          setMerchStoreError(true);
        })
    } else {
      setTimeout(() => setLoading(false), 200);
    }
  }, [id, fetchMerchStore, sortOptionChosen])

  // listen for page changes until the form is rendered before adding the change event listener
  useEffect(() => {
    const form = document.getElementById("create-edit-merch-store-form");
    if (form && !formFound) {
      form.addEventListener("input", function (e) {
        if ((e.target as HTMLInputElement).name === "store-url") {
          setTempStoreUrl((e.target as HTMLInputElement).value);
        }
        if ((e.target as HTMLInputElement).value !== (e.target as HTMLInputElement).defaultValue) {
          setMerchStoreEditActive(true);
        }
      });
      setFormFound(true);
    }
  })
  // listen for custom form element changes
  useEffect(() => {
    // If user navigates to a specific order
    if (merchStore) {

      if (userRole && userEmail && id) {
        switch (userRole) {
          case "MERCHOLOGIST":
            if (merchStore.merchologist !== userEmail) {
              history.push("/");
            }
            break;
          case "ADMIN":
            if (merchStore.groupAdmin !== userEmail) {
              history.push("/");
            }
            break;
        }
      }
      if (merchStoreLogo && (merchStoreLogo[0] !== merchStore.storeLogo)) {
        setMerchStoreEditActive(true);
      }
      if (merchStore.status !== "draft") {
        setDisabledPassword(true);
      }
      if (merchStore.status === "closed") {
        setMerchStoreEditActive(false);
      }

      setSelectedStatus(merchStore.status);

      setLoading(false);
    }

    const isMobile = window.matchMedia("only screen and (max-width: 760px)").matches;
    setIsMobile(isMobile);
  }, [merchStore, merchStoreLogo, passwordSelected, userRole, userEmail, isMobile, accentColor])

  const handleConfirmationModalClose = () => { setConfirmationModalOpen(false) };
  const handleConfirmationModalConfirm = () => {
    setConfirmationModalOpen(false);
    processMerchStore(formData);
  }

  if (loading) {
    return <div className="h-screen flex items-center justify-center">
      <img src={LoadingGif} alt="loading flask" width="100px" />
    </div>
  }

  return (
    <div className="relative flex justify-center">
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={handleConfirmationModalClose}
        image={merchStoreLogo}
        headlineText="Are you sure you want to set your MerchStore to active?"
        bodyText="Setting your store to Active will notify your members that your MerchStore is open for business."
        onConfirmation={handleConfirmationModalConfirm}
        onCancellation={handleConfirmationModalClose}
      />
      <div className="flex flex-col p-0 lg:px-20 lg:py-8 w-full items-center">
        <div className="mb-7 pt-3 px-5 lg:px-7 lg: pt-0 w-full flex justify-center flex-0">
          <div className="w-full max-w-7xl">
            {merchStore && (
              <p className="px-7 ml-4 md:ml-0 lg:px-0 text-xs lg:text-sm text-merch-dark-gray font-bold opacity-50">{merchStore.storeCode}</p>
            )}
            <div className={"flex items-center ml-4 md:ml-0 justify-between"}>
              {nameEditActive ? (
                <div className="px-7 lg:px-0 flex items-center">
                  <input className="bg-transparent text-lg lg:text-3xl text-merch-dark-gray font-bold pl-0.5" type="text" autoFocus placeholder={newMerchStoreName ? newMerchStoreName : merchStore ? merchStore.name : "Untitled"} value={merchStoreName} onChange={(e) => { handleMerchStoreNameChange(e) }} onBlur={(e) => { handleMerchStoreNameSubmit() }} />
                  <button disabled={merchStoreName ? false : true} className={`opacity-50 ml-1.5 ${merchStoreName ? "" : "cursor-default"}`} onClick={() => { handleMerchStoreNameSubmit() }}>
                    <IconContext.Provider
                      value={merchStoreName ? { color: "limeGreen" } : { color: "grey" }}
                    >
                      <div>
                        <MdDone />
                      </div>
                    </IconContext.Provider>
                  </button>
                </div>
              ) : (
                <div className="px-7 lg:px-0 flex items-center">
                  <div onClick={() => { setNameEditActive(true) }} className="cursor-pointer text-lg lg:text-3xl text-merch-dark-gray font-bold">{newMerchStoreName ? newMerchStoreName : merchStore ? merchStore.name : "Untitled"}</div>
                  <div onClick={() => { setNameEditActive(true) }} className="cursor-pointer flex ml-1.5 h-full"><MdEdit /></div>
                </div>
              )}
              <div>
                <div className="md:hidden">
                  {merchStore && merchStore.status !== "closed" &&
                    <ActionDropdown
                      title="Actions"
                      list={[
                        {
                          title: `Reassign`,
                          id: `reassign`,
                          action: () => alert('Reassign')

                        },
                        {
                          title: `Close Store`,
                          id: `closeStore`,
                          action: () => alert('Close Store')

                        },
                        {
                          title: `Delete Store`,
                          id: `deleteStore`,
                          action: () => alert('Delete Store')

                        },
                      ]}
                    />
                  }

                </div>
                <div className="hidden md:flex">
                  {userRole === 'ADMIN' ?
                    <CTA size="standard" type="secondary" classes="text-13 h-45 mr-3 rounded-lg" icon={<MdNotificationsNone size={iconSize} />} onClick={() => alert('Send Reminder!')}>Send Reminder</CTA>
                    :
                    <ActionDropdown
                      className="mr-3 text-13 rounded-lg py-2 px-3 h-full"
                      title="Actions"
                      list={[
                        {
                          title: `Reassign`,
                          id: `reassign`,
                          action: () => alert('Reassign')

                        },
                        {
                          title: `Close Store`,
                          id: `closeStore`,
                          action: () => alert('Close Store')

                        },
                        {
                          title: `Delete Store`,
                          id: `deleteStore`,
                          action: () => alert('Delete Store')

                        },
                      ]}
                    />
                  }
                  <CTA size="standard" type="secondary" classes="rounded-lg " icon={<MdOpenInNew size='18px' />} onClick={() => alert('Share?')}></CTA>
                </div>

              </div>
            </div>
            <MerchStoreTiles
              merchStore={merchStore}
              members={members}
            />
          </div>
        </div>
        <Card>
          {merchStoreError ? (
            <h3>TODO: BUILD AN ERROR VIEW</h3>
          ) : selectedProduct ? (
            <MerchStoreProductEdit
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              isMobile={isMobile}
              artworkOptions={merchStore.artworkOptions}
              userRole={userRole}
              storeCode={merchStore.storeCode}
              subdomain={merchStore.subdomain}
              fetchCollection={fetchCollection}
              deleteProduct={deleteProduct}
            />
          ) : (
            <div className="flex flex-col lg:flex-row w-full">
              <div className="md:p-8 p-0 w-full">
                <div className="md:p-0 p-6 flex items-start justify-between mb-4 md:mb-12">
                  <Tabs>
                    <Tab onClick={handleTabSelect} active={active === 0} id={0}>
                      Members
                    </Tab>
                    <Tab onClick={handleTabSelect} active={active === 1} id={1}>
                      Products
                    </Tab>
                    <Tab onClick={handleTabSelect} active={active === 2} id={2}>
                      Orders
                    </Tab>
                  </Tabs>
                  <div>
                    <Content active={active === 0}>
                      {id !== "new"
                        ? <CTA
                          type={'tertiary'}
                          size={'standard'}
                          icon={invite ? <MdClose size="20px" /> : <IoMdPersonAdd size="20px" />}
                          onClick={() => { setInvite(!invite) }}
                        >
                          {isMobile ? "" : "Add Members"}
                        </CTA>
                        : ''}
                    </Content>
                    {userRole !== "ADMIN" &&
                      <Content active={active === 1}>
                        <CTA
                          type={'tertiary'}
                          size={'standard'}
                          icon={<MdAdd size="20px" />}
                          onClick={() => { openAddProductsModal() }}>
                          {isMobile ? "" : "Add Products"}
                        </CTA>
                        <ProductSearch
                          open={addProductsModalOpen}
                          onClose={onModalClose}
                          storeCode={merchStore.storeCode}
                          storeSubdomain={merchStore.subdomain}
                          onProductsAdded={onProductsAdded}
                          disabledProductIds={disabledProductIds} />

                      </Content>
                    }
                  </div>
                </div>
                <div>
                  <Content active={active === 0}>
                    <MerchStoreMemberList
                      showAddMember={invite}
                      order={merchStore}
                      uniqueId={id}
                      setSortOptionChosen={setSortOptionChosen}
                      fetchMembers={fetchMembers}
                      memberAndSubmissionDetails={members}
                      onMembersAdded={onMembersAdded}>
                    </MerchStoreMemberList>
                  </Content>
                  <Content active={active === 1}>
                    {merchStore &&
                      <MerchStoreProductList
                        products={merchstoreCollection}
                        setSelectedProduct={setSelectedProduct}
                        isMobile={isMobile}
                        deleteProduct={deleteProduct}
                        isLoading={productListLoading}
                      />
                    }
                  </Content>
                  <Content active={active === 2}>
                    {merchStore &&
                      <div className="flex items-center border border-gray-300 rounded-md h-80 bg-gray-100">
                        <div className="flex justify-center items-center h-40 w-40 bg-gray-50 border border-gray-300 rounded-md text-gray-400 text-2xl mx-20"><MdInfo /></div>
                        <div className="font-bold">TODO: Orders Tab</div>
                      </div>}
                  </Content>
                </div>
              </div>
              <div className="p-8 bg-gray-50 lg:min-w-order-sidebar flex flex-col items-start rounded-tr-24 rounded-br-24">
                <form onSubmit={handleSubmit(handleMerchStoreSave)} className="w-full" id="create-edit-merch-store-form">
                  <FormSelect
                    name="merch-store-status"
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    label="Store Status"
                    emptyText="Status"
                    tooltip="Keep your group MerchStore set to Draft until you are ready to go live. Setting your store to Active will notify your members that your group order is available."
                    helpText="Required"
                    default={merchStore ? merchStore.status : "draft"}
                    options={merchStoreStatusOptions}
                    onChange={handleStatusChange}
                    disabled={merchStore && merchStore.status === 'closed'}
                  />

                  <FormInput
                    disabled={merchStore}
                    inputStyle="two"
                    name="store-url"
                    defaultValue={merchStore ? merchStore.subdomain : ""}
                    placeholder="amazon-team-store"
                    type="text"
                    label="Store URL"
                    required={merchStore ? false : true}
                    errors={errors}
                    register={register}
                    tooltip="Cannot be modified once it is initially set."
                    linkSubText={(tempMerchStoreUrl ? tempMerchStoreUrl : merchStore ? merchStore.subdomain : "amazon-team-store") + "." + domainName}
                  ></FormInput>

                  <CloudinaryFileInput
                    error={storeLogoError}
                    name="store-logo"
                    type="one"
                    updateContent={handleLogoChange}
                    content={merchStoreLogo ? merchStoreLogo : []} maxFiles={1}
                    label="Store Logo"
                    placeholder="JPEGs, PNGs or SVGs"
                    tooltip="Logo should have a pixel height dimension of 90px or more."
                    helpText="Min. height of 88px"
                    disabled={merchStore && merchStore.status === 'closed'}
                    cloudinarySource="merchstores"
                  ></CloudinaryFileInput>

                  <CloudinaryFileInput
                    error={storeBannerImageError}
                    name="store-banner-image"
                    type="one"
                    updateContent={handleBannerImageChange}
                    content={storeBannerImage ? storeBannerImage : []} maxFiles={1}
                    label="Banner Image"
                    placeholder="JPEGs, PNGs or SVGs"
                    tooltip="Banner image should have a pixel height dimension of 680px or more."
                    helpText="Min. height of 680px"
                    disabled={merchStore && merchStore.status === 'closed'}
                    cloudinarySource="merchstores"
                  ></CloudinaryFileInput>

                  <FormInput
                    textArea={true}
                    inputStyle="two"
                    name="store-message"
                    defaultValue={merchStore ? merchStore.storeMessage : ""}
                    placeholder="Message"
                    type="text"
                    label="Store Message"
                    required={true}
                    errors={errors}
                    register={register}
                    tooltip="This messaging displays on your Merch Store and provides some context as to what the user can do and see."
                    disabled={merchStore && merchStore.status === 'closed'} />

                  <ColorPickerInput
                    label='Store Accent Color'
                    id='store-accent-color'
                    onChange={(color: string) => onAccentColorChange(color)}
                    register={register}
                    errors={errors}
                    required={false}
                    name="store-accent-color"
                    defaultValue={merchStore && merchStore.accentColor ? merchStore.accentColor : '#000000'}
                    tooltip='.' />

                  <SchedulerInput
                    disabled={userRole === "ADMIN" ? true : (merchStore && merchStore.status === 'closed') ? true : false}
                    frequencyName='store-processing-schedule'
                    dateInputName='store-next-processing-date'
                    frequencyDefaultValue={merchStore ? merchStore.processingSchedule : ''}
                    dateInputDefaultValue={merchStore && merchStore.nextProcessingDate ? convertDate(merchStore.nextProcessingDate) : ''}
                    label='Processing Schedule'
                    frequencyLabel='Processes'
                    dateInputLabel='Next Processing Date'
                    required={true}
                    errors={errors}
                    register={register}
                    helpText="Required"
                    tooltip="."
                    setValue={setValue}
                    onChange={handleProcessingSchedule} />

                  <div className={`${!passwordSelected ? "mb-18" : ""}`}>
                    <Checkbox
                      label="Require Password"
                      className="items-center"
                      onChange={handlePasswordSelect}
                      isSelected={passwordSelected}
                      tooltip="This option requires the front-end user to input a specified password prior to entering the Merch Store."
                      disabled={disabledPasswords} />
                  </div>
                  {passwordSelected && (
                    <FormInput inputStyle="two" name="store-password" disabled={!passwordSelected || (merchStore && merchStore.status === 'closed')} defaultValue={merchStore && merchStore.password ? merchStore.password : ""} type="text" placeholder="Enter Password" label="" required={false} errors={errors} register={register}></FormInput>
                  )}
                  <div className="flex w-full justify-between">
                    <div className="flex w-full">
                      <CTA disabled={merchStore ? !merchStoreEditActive : false} stretch={true} formSubmit={true} size="standard" type="primary">{merchStore ? "Update MerchStore" : "Save MerchStore"}</CTA>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div >
  )
}
